/**
 * ChannelPriceEditorDialog — 框架通道商品行的原生成交价编辑（设计稿 B3 / B4，A8 §10.3）。
 *
 * - 读：`GET /product-metadata/:id/channel-prices`（viewer+）。读取失败**不**显示成「未定价」，保存禁用直到读取成功；
 * - 写：`PUT` 同路径（root / admin + mfa），整组替换；单币种（能力声明 `currency_rule` 闭集只有 `SINGLE_PRICE_ROW`），
 *   UI 只有一个币种槽、不提供「添加币种」，改币种 = 替换这一行；`prices: []` = 清除（该通道不可售）；
 * - 只录 `amount_minor_units`（正整数串，不经 Number），输入时同步显示换算后的金额供核对；
 * - 已有价格时保存先弹「改价」确认（只影响新订单），清除走 danger 确认；
 * - 幂等：每个意图一枚 UUID v7 `Idempotency-Key`，失败重试且内容不变时复用，内容变了换新；价格没有版本栅栏，
 *   保存成功后以服务端响应覆盖本地缓存，再刷新商品列表（`channel_prices_summary` / `validation_issues`）；
 * - operator / viewer 只读：控件禁用并写明原因（operator 可以建行，但不能定价）。
 */

import type { NativeChannelPricesResponse } from '@novel-admin/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type ChannelPriceErrorView,
  amountMinorUnitsError,
  currencyMinorExponent,
  currencyOptions,
  describeChannelPriceError,
  formatChannelPriceMoney,
  formatChannelPriceTag,
  minorUnitHint,
} from '~/data/adapters/channel-price-adapter';
import { channelStoreLabel } from '~/data/adapters/product-metadata-grouping';
import type { ProductMetadataRow } from '~/data/queries';
import { productMetadataApi } from '~/hooks/use-product-metadata';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { newIdempotencyKeyV7 } from '~/lib/idempotency-key';
import { useAuthStore } from '~/stores/auth-store';
import { ConfirmDialog, Dialog, DialogBody } from '~/ui/Dialog';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

const ADMIN_ONLY_TITLE = '需要 admin 权限';

/** 价格读数的 query key：挂在商品列表 key 之下，列表失效时一并重读 */
export function channelPricesQueryKey(appId: string, productMetadataId: string) {
  return ['product-metadata', appId, 'channel-prices', productMetadataId] as const;
}

/** 保存失败的提示块（neg-soft 底 + 机读定位等宽小字）*/
function SaveErrorBlock({ error }: { error: ChannelPriceErrorView }) {
  return (
    <div
      role="alert"
      style={{
        padding: '10px 12px',
        background: 'var(--neg-soft)',
        borderRadius: 'var(--radius)',
        fontSize: 11.5,
        lineHeight: 1.55,
      }}
    >
      <div style={{ fontWeight: 500 }}>{error.title}</div>
      {error.hint && <div style={{ color: 'var(--fg-muted)' }}>{error.hint}</div>}
      <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 2 }}>
        {error.code}
      </div>
    </div>
  );
}

export function ChannelPriceEditorDialog({
  appId,
  row,
  productName,
  onClose,
}: {
  appId: string;
  /** 框架通道商品行（IAP / STRIPE 行不应打开本弹窗）*/
  row: ProductMetadataRow;
  /** 支付商品名（标题 / 确认文案用）*/
  productName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canPrice = role === 'root' || role === 'admin';
  const channelName = channelStoreLabel(row.paymentMethod, undefined);

  const pricesQuery = useQuery({
    queryKey: channelPricesQueryKey(appId, row.id),
    queryFn: () => productMetadataApi.getChannelPrices(row.id),
    // 每次打开都向服务端重读：价格无版本栅栏，拿缓存旧值回填再保存会把别人刚改的价改回去
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const current = pricesQuery.data?.prices[0] ?? null;

  const [currency, setCurrency] = useState('EUR');
  const [amount, setAmount] = useState('');
  // 本次打开后的重读完成才回填一次（不吃缓存旧值）；回填前输入与保存都禁用，之后运营的输入不被后台重读覆盖
  const [seeded, setSeeded] = useState(false);
  // 必须是本次挂载后的成功读取：离线时重读被挂起（paused），isFetching 为 false，只看它会吃到缓存旧值
  const loaded =
    pricesQuery.isSuccess && pricesQuery.isFetchedAfterMount && pricesQuery.fetchStatus === 'idle';
  useEffect(() => {
    if (seeded || !loaded || !pricesQuery.data) return;
    setSeeded(true);
    const first = pricesQuery.data.prices[0];
    if (first) {
      setCurrency(first.currency);
      setAmount(first.amount_minor_units);
    }
  }, [seeded, loaded, pricesQuery.data]);
  const ready = seeded && pricesQuery.isSuccess;

  const [confirming, setConfirming] = useState<'change' | 'clear' | null>(null);
  const [saveError, setSaveError] = useState<ChannelPriceErrorView | null>(null);
  // 幂等键按「意图内容」绑定：内容不变的重试复用，内容变了换新
  const idempotency = useRef<{ fingerprint: string; key: string } | null>(null);

  const options = useMemo(() => {
    const all = currencyOptions();
    return current && !all.includes(current.currency) ? [current.currency, ...all] : all;
  }, [current]);

  const amountError = amount === '' ? null : amountMinorUnitsError(amount, currency);
  const amountValid = amount !== '' && amountError === null;
  const changed =
    current === null ||
    current.currency !== currency ||
    current.amount_minor_units !== amount.trim();

  const saveMutation = useMutation({
    mutationFn: async (
      prices: { currency: string; amount_minor_units: string }[],
    ): Promise<NativeChannelPricesResponse> => {
      const fingerprint = JSON.stringify(prices);
      if (idempotency.current?.fingerprint !== fingerprint) {
        idempotency.current = { fingerprint, key: newIdempotencyKeyV7() };
      }
      return productMetadataApi.putChannelPrices(row.id, { prices }, idempotency.current.key);
    },
    onSuccess: async (response, prices) => {
      idempotency.current = null;
      setSaveError(null);
      // 无版本栅栏：以服务端响应为准覆盖本地，再刷新列表的价格汇总与不可售原因
      queryClient.setQueryData(channelPricesQueryKey(appId, row.id), response);
      toast(
        prices.length === 0
          ? `已清除 ${productName} 的 ${channelName} 价格`
          : `已保存 ${productName} 的 ${channelName} 价格`,
        { tone: prices.length === 0 ? 'warn' : 'pos', icon: 'IconCheck' },
      );
      await queryClient.invalidateQueries({ queryKey: ['product-metadata', appId], exact: true });
      onClose();
    },
    onError: async (err) => {
      const view = describeChannelPriceError(err, channelName);
      if (!view.reuseIdempotencyKey) idempotency.current = null;
      setSaveError(view);
      if (view.refreshList) {
        await queryClient.invalidateQueries({ queryKey: ['product-metadata', appId], exact: true });
      }
    },
  });
  const pending = saveMutation.isPending;

  const submitPrice = () => saveMutation.mutate([{ currency, amount_minor_units: amount.trim() }]);
  const onSave = () => {
    if (current !== null) setConfirming('change');
    else submitPrice();
  };

  const canSave = canPrice && ready && amountValid && changed && !pending && currency !== '';
  const nextPrice = { currency, amount_minor_units: amount.trim() };

  return (
    <>
      <Dialog
        width={540}
        icon="IconTag"
        title={`${channelName} 价格 · ${productName}`}
        subtitle="原生成交价 · 单币种 · 只影响新订单"
        // 内层确认弹窗打开时，Esc 只关内层
        closeOnEsc={confirming === null}
        onClose={() => {
          if (!pending) onClose();
        }}
        footer={
          <>
            <button
              type="button"
              className={canPrice ? 'btn btn-danger' : 'btn'}
              disabled={!canPrice || !ready || current === null || pending}
              title={!canPrice ? ADMIN_ONLY_TITLE : current === null ? '当前未定价' : undefined}
              onClick={() => setConfirming('clear')}
            >
              清除价格
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn" disabled={pending} onClick={onClose}>
              {canPrice ? '取消' : '关闭'}
            </button>
            <button
              type="button"
              className={canPrice ? 'btn btn-accent' : 'btn'}
              disabled={!canSave}
              title={!canPrice ? ADMIN_ONLY_TITLE : undefined}
              onClick={onSave}
            >
              {pending ? '保存中…' : '保存价格'}
            </button>
          </>
        }
      >
        <DialogBody gap={14}>
          {pricesQuery.isError ? (
            <div
              className="card"
              role="alert"
              style={{ padding: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}
            >
              <Icons.IconAlert size={14} style={{ color: 'var(--neg)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>价格读取失败</div>
                <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
                  不显示为「未定价」，保存按钮禁用直到读取成功。
                </div>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => pricesQuery.refetch()}
                >
                  重试
                </button>
              </div>
            </div>
          ) : !ready ? (
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>正在读取价格…</div>
          ) : current ? (
            <div className="hstack" style={{ gap: 8, fontSize: 11.5, color: 'var(--fg-muted)' }}>
              当前{' '}
              <span className="mono" style={{ color: 'var(--fg)' }}>
                {current.currency} {current.amount_minor_units}
              </span>{' '}
              {/* 存量不可定价币种没有小数位定义，不给主单位换算（字典 65 §9） */}
              {currencyMinorExponent(current.currency) === null ? (
                <span>（币种不可定价，无主单位换算）</span>
              ) : (
                <>
                  ={' '}
                  <span className="mono" style={{ color: 'var(--fg)' }}>
                    {formatChannelPriceMoney(current)}
                  </span>
                </>
              )}{' '}
              · 更新于 <span className="mono">{fmtDateTime(current.updated_at)}</span>
            </div>
          ) : (
            <div className="hstack" style={{ gap: 8 }}>
              <span className="chip chip-neg" style={{ fontSize: 10.5 }}>
                未定价 · C 端不可售
              </span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', gap: 12 }}>
            <div>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: canPrice ? undefined : 'var(--fg-muted)',
                }}
              >
                币种
                {canPrice && <span style={{ color: 'var(--neg)', marginLeft: 3 }}>*</span>}
              </div>
              <div style={{ marginTop: 6 }}>
                <Select
                  className="input mono"
                  value={currency}
                  searchable
                  searchPlaceholder="输入币种码筛选，如 EUR"
                  disabled={!canPrice || !ready || pending}
                  onChange={(e) => {
                    setCurrency(e.target.value);
                    setSaveError(null);
                  }}
                >
                  {options.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div>
              <label
                htmlFor="channel-price-amount"
                style={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: canPrice ? undefined : 'var(--fg-muted)',
                }}
              >
                金额（最小单位）
                {canPrice && <span style={{ color: 'var(--neg)', marginLeft: 3 }}>*</span>}
              </label>
              <input
                id="channel-price-amount"
                className="input mono"
                inputMode="numeric"
                value={amount}
                placeholder={canPrice ? '如 299' : '—'}
                aria-invalid={amountError !== null || undefined}
                disabled={!canPrice || !ready || pending}
                style={{
                  marginTop: 6,
                  ...(amountError !== null ? { borderColor: 'var(--neg)' } : {}),
                }}
                onChange={(e) => {
                  setAmount(e.target.value.replace(/\s/g, ''));
                  setSaveError(null);
                }}
              />
              {amountError !== null ? (
                <div style={{ fontSize: 11.5, color: 'var(--neg)', marginTop: 6 }}>
                  {amountError}
                </div>
              ) : amountValid ? (
                <div
                  className="hstack"
                  style={{ gap: 6, marginTop: 5, fontSize: 11.5, color: 'var(--fg-muted)' }}
                >
                  核对：
                  <span className="mono" style={{ color: 'var(--fg-strong)', fontWeight: 600 }}>
                    {formatChannelPriceMoney(nextPrice)}
                  </span>
                  <span>{minorUnitHint(currency)}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div
            className="card-flat"
            style={{
              padding: '10px 12px',
              fontSize: 11.5,
              color: 'var(--fg-muted)',
              lineHeight: 1.6,
            }}
          >
            <div style={{ color: 'var(--fg)', fontWeight: 500, marginBottom: 2 }}>
              为什么只能有一个币种
            </div>
            {channelName} 的能力声明规定每个商品只有一行价格（
            <span className="mono">SINGLE_PRICE_ROW</span>
            ）。没有「添加币种」；改币种就是替换这一行。
          </div>

          {!canPrice && (
            <div
              className="hstack"
              style={{
                gap: 8,
                padding: '8px 10px',
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                fontSize: 11.5,
                color: 'var(--fg-muted)',
              }}
            >
              <Icons.IconLock size={12} style={{ flexShrink: 0 }} />
              定价需要 root / admin 权限并完成 MFA。operator 可以建行，但不能定价。
            </div>
          )}

          {saveError && <SaveErrorBlock error={saveError} />}
        </DialogBody>
      </Dialog>

      {confirming === 'change' && current && (
        <ConfirmDialog
          tone="warn"
          title={`把 ${productName} 的 ${channelName} 价格改为 ${formatChannelPriceMoney(nextPrice)}`}
          message="只影响新订单；进行中的订单按下单时的价格结算。"
          details={[
            `${current.currency} ${current.amount_minor_units} → ${nextPrice.currency} ${nextPrice.amount_minor_units}`,
          ]}
          confirmLabel="确认改价"
          onConfirm={submitPrice}
          onClose={() => setConfirming(null)}
        />
      )}
      {confirming === 'clear' && current && (
        <ConfirmDialog
          tone="danger"
          title={`清除 ${productName} 的 ${channelName} 价格`}
          message={`清除后该商品在 ${channelName} 不可售（未定价），C 端不再展示这一行。进行中的订单不受影响。`}
          details={[formatChannelPriceTag(current)]}
          confirmLabel="清除价格"
          onConfirm={() => saveMutation.mutate([])}
          onClose={() => setConfirming(null)}
        />
      )}
    </>
  );
}
